import type {
  DecksResponse,
  DeckResponse,
  DeckStats,
  DeckCollectionStatus,
  SharedDeckResponse,
  AddMissingToBuylistResponse,
  ShareTokenResponse,
} from '@/types'
import { request } from './client'

export async function getDecks(): Promise<DecksResponse> {
  return request<DecksResponse>('/decks')
}

export async function getDeck(id: number): Promise<DeckResponse> {
  return request<DeckResponse>(`/decks/${id}`)
}

export async function getDeckStats(id: number): Promise<DeckStats> {
  return request<DeckStats>(`/decks/${id}/stats`)
}

export async function getDeckCollectionStatus(id: number): Promise<DeckCollectionStatus> {
  return request<DeckCollectionStatus>(`/decks/${id}/collection_status`)
}

export async function createDeck(
  data: {
    name: string
    description?: string
    leaderCardId: number
    isPublic?: boolean
  },
  idempotencyKey?: string,
): Promise<DeckResponse> {
  return request<DeckResponse>('/decks', {
    method: 'POST',
    body: data,
    idempotencyKey,
  })
}

export async function updateDeck(
  id: number,
  data: { name?: string; description?: string; isPublic?: boolean },
  idempotencyKey?: string,
): Promise<DeckResponse> {
  return request<DeckResponse>(`/decks/${id}`, {
    method: 'PATCH',
    body: data,
    idempotencyKey,
  })
}

export async function deleteDeck(id: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/decks/${id}`, { method: 'DELETE', idempotencyKey })
}

export async function addCardToDeck(deckId: number, cardId: number, quantity: number = 1, idempotencyKey?: string) {
  return request(`/decks/${deckId}/cards`, {
    method: 'POST',
    body: { cardId, quantity },
    idempotencyKey,
  })
}

export async function updateDeckCard(deckId: number, cardId: number, quantity: number, idempotencyKey?: string) {
  return request(`/decks/${deckId}/cards/${cardId}`, {
    method: 'PATCH',
    body: { quantity },
    idempotencyKey,
  })
}

export async function removeDeckCard(deckId: number, cardId: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/decks/${deckId}/cards/${cardId}`, { method: 'DELETE', idempotencyKey })
}

export async function importDeck(deckText: string, name?: string, idempotencyKey?: string): Promise<DeckResponse> {
  return request<DeckResponse>('/decks/import', {
    method: 'POST',
    body: { deckText, name },
    idempotencyKey,
  })
}

export async function duplicateDeck(id: number, idempotencyKey?: string): Promise<DeckResponse> {
  return request<DeckResponse>(`/decks/${id}/duplicate`, { method: 'POST', idempotencyKey })
}

export async function addMissingToBuylist(
  deckId: number,
  idempotencyKey?: string,
): Promise<AddMissingToBuylistResponse> {
  return request<AddMissingToBuylistResponse>(`/decks/${deckId}/add_missing_to_buylist`, {
    method: 'POST',
    idempotencyKey,
  })
}

export async function regenerateDeckShareToken(id: number, idempotencyKey?: string): Promise<ShareTokenResponse> {
  return request<ShareTokenResponse>(`/decks/${id}/regenerate_token`, { method: 'POST', idempotencyKey })
}

export async function getSharedDeck(shareToken: string): Promise<SharedDeckResponse> {
  return request<SharedDeckResponse>(`/shared/decks/${shareToken}`)
}
